import type {
  BotLevel,
  DemocracyVoteMode,
  GameDto,
  GameMode,
  GameSummaryDto,
  Side,
  TimeControl,
  UserDto,
} from "@chess/shared";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    // The session cookie is httpOnly, so every call has to opt into sending it.
    credentials: "same-origin",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as
      | { code?: string; message?: string }
      | null;
    throw new ApiError(
      response.status,
      body?.code ?? "INTERNAL",
      body?.message ?? `Request failed (${response.status})`,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Returns the signed-in user, or null when there's no valid session cookie. */
export async function fetchMe(): Promise<UserDto | null> {
  try {
    return await request<UserDto>("/api/me");
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export function createSession(username: string): Promise<UserDto> {
  return request<UserDto>("/api/session", {
    method: "POST",
    body: JSON.stringify({ username }),
  });
}

export function logout(): Promise<void> {
  return request<void>("/api/session/logout", { method: "POST" });
}

export function listGames(): Promise<GameSummaryDto[]> {
  return request<GameSummaryDto[]>("/api/games");
}

export function createGame(
  side: Side | "random" = "random",
  timeControl: TimeControl | null = null,
  mode: GameMode = "1v1",
  democracyVoteMode: DemocracyVoteMode = "random",
): Promise<GameDto> {
  return request<GameDto>("/api/games", {
    method: "POST",
    body: JSON.stringify({ side, timeControl, mode, democracyVoteMode }),
  });
}

export function joinGame(gameId: string, side?: Side | "random"): Promise<GameDto> {
  return request<GameDto>(`/api/games/${gameId}/join`, {
    method: "POST",
    ...(side ? { body: JSON.stringify({ side }) } : {}),
  });
}

export function readyGame(gameId: string): Promise<GameDto> {
  return request<GameDto>(`/api/games/${gameId}/ready`, { method: "POST" });
}

export function updateDemocracyLobbySettings(
  gameId: string,
  timeControl: TimeControl | null,
  democracyVoteMode: DemocracyVoteMode,
): Promise<GameDto> {
  return request<GameDto>(`/api/games/${gameId}/settings`, {
    method: "PATCH",
    body: JSON.stringify({ timeControl, democracyVoteMode }),
  });
}

export function listOpenDemocracyGames(): Promise<GameSummaryDto[]> {
  return request<GameSummaryDto[]>("/api/games/open/democracy");
}

export function closeLobby(gameId: string): Promise<void> {
  return request<void>(`/api/games/${gameId}`, { method: "DELETE" });
}

export function reorderTeam(
  gameId: string,
  side: Side,
  userIds: readonly string[],
): Promise<GameDto> {
  return request<GameDto>(`/api/games/${gameId}/order`, {
    method: "PUT",
    body: JSON.stringify({ side, userIds }),
  });
}

export function addBot(
  gameId: string,
  side: Side | "random",
  level: BotLevel,
): Promise<GameDto> {
  return request<GameDto>(`/api/games/${gameId}/bots`, {
    method: "POST",
    body: JSON.stringify({ side, level }),
  });
}

export function removeBot(gameId: string, botUserId: string): Promise<GameDto> {
  return request<GameDto>(`/api/games/${gameId}/bots/${botUserId}`, { method: "DELETE" });
}

export function fetchGame(gameId: string): Promise<GameDto> {
  return request<GameDto>(`/api/games/${gameId}`);
}
